import { Fragment, useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import { useAnalyticsWrapper } from "../../../../../store/store";
import { Child, Container, Header, LayerContent } from "../style";
import { ChartContainer, NoData } from "../../style";
import { barOptions, barColors, handlePointClicked } from "../formUtils";
import SectionModal from "./SectionModal";
import {
  Grid,
  GridColumn,
  GridColumnProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import {
  CenteredWholeNumCell,
  HeaderCenteredCell,
} from "../../../../../components/DynamicGrid/customCells";
import { TotalRow } from "../../../../../components/DynamicGrid/customRows";
import {
  handleCSVDownload,
  handlePDFDownload,
} from "../../../../../utils/helpers/pdfCsvUtils";
import { pressureUlcerStagingDetailCols } from "../../dummyData/colums";

// Section 4 — Pressure Ulcer Staging by month (PU-only; flag on III/IV/sDTI/Unstageable).
// Reads woundAnalysisIncidents.pressureUlcerStaging (months, stagingByMonth, drillDownByStage).

// The vertical "Total" and "%" columns are tinted the same purple as the horizontal total row
// (.grid-total-row → #b580ff4d / #643375) so they read clearly as totals.
const TotalColumnCell = (props: GridCustomCellProps) => {
  const { dataItem, field, tdProps } = props;
  const raw = dataItem[field || ""] ?? 0;
  const value = field === "percentage" ? `${raw}%` : raw;
  return (
    <td
      {...tdProps}
      style={{
        ...tdProps?.style,
        textAlign: "center",
        background: "#b580ff4d",
        color: "#643375",
        fontWeight: 600,
      }}
    >
      {value}
    </td>
  );
};
const PressureUlcerStaging = ({ setClickedIncident }: any) => {
  const [months, setMonths] = useState<string[]>([]);
  const [tableData, setTableData] = useState<any[]>([]);
  const [grid, setGrid] = useState<any>([]);
  const [barData, setBarData] = useState<any>(null);
  const [stage, setStage] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [stageDetails, setStageDetails] = useState<any>([]);

  const { startDate, endDate, woundFacility, woundAnalysisIncidents } =
    useAnalyticsWrapper((state) => ({
      startDate: state.startDate,
      endDate: state.endDate,
      woundFacility: state.woundFacility,
      woundAnalysisIncidents: state.woundAnalysisIncidents,
    }));

  useEffect(() => {
    const section = woundAnalysisIncidents?.pressureUlcerStaging;
    const stagingByMonth = section?.stagingByMonth;
    const monthLabels: string[] = section?.months ?? [];

    if (stagingByMonth?.length) {
      const rows = stagingByMonth.map((row: any) => {
        const flat: Record<string, any> = {
          stage: row.stage,
          total: row.total,
          percentage: row.percentage,
        };
        monthLabels.forEach((m) => {
          flat[m] = row.monthlyCounts?.[m] ?? 0;
        });
        return flat;
      });
      const totalRow: Record<string, any> = {
        stage: "Total",
        total: rows.reduce((s: number, r: any) => s + (r.total ?? 0), 0),
        percentage: 100,
      };
      monthLabels.forEach((m) => {
        totalRow[m] = rows.reduce((s: number, r: any) => s + (r[m] ?? 0), 0);
      });
      rows.push(totalRow);

      setMonths(monthLabels);
      setTableData(rows);
      setBarData({
        labels: stagingByMonth.map((r: any) => r.stage),
        datasets: [
          {
            label: "Pressure Ulcer Staging",
            backgroundColor: barColors(stagingByMonth.length),
            borderWidth: 1,
            data: stagingByMonth.map((r: any) => r.total ?? 0),
          },
        ],
      });
    } else {
      setMonths([]);
      setTableData([]);
      setBarData(null);
    }
  }, [woundAnalysisIncidents]);

  const title = "Pressure Ulcer Staging";

  const handleBarClicked = (chart: any) => {
    if (!chart.length) return;
    const label = handlePointClicked(chart, barData?.labels);
    setStage(label);
    setStageDetails(
      woundAnalysisIncidents?.pressureUlcerStaging?.drillDownByStage?.[label] || []
    );
    setShowModal(true);
  };

  useEffect(() => {
    const cellCountProps = {
      cells: { data: CenteredWholeNumCell, headerCell: HeaderCenteredCell },
    };
    // Fixed widths so the grid overflows and scrolls horizontally once there are enough month columns.
    const newGrid = (
      <Fragment>
        <Grid data={tableData || []} scrollable="scrollable" rows={{ data: TotalRow }}>
          <GridColumn field="stage" title="Stage" width="110px" />
          {months.map((m) => (
            <GridColumn key={m} field={m} title={m} width="70px" {...(cellCountProps as GridColumnProps)} />
          ))}
          <GridColumn
            field="total"
            title="Total"
            width="70px"
            cells={{ data: TotalColumnCell, headerCell: HeaderCenteredCell }}
          />
          <GridColumn
            field="percentage"
            title="%"
            width="60px"
            cells={{ data: TotalColumnCell, headerCell: HeaderCenteredCell }}
          />
        </Grid>
      </Fragment>
    );
    setGrid(newGrid);
  }, [tableData, months]);

  return (
    <LayerContent>
      <Header>{title}</Header>
      <Container>
        <Child flex="55%">
          <div style={{ overflowX: "auto", width: "100%" }}>{grid}</div>
        </Child>
        <Child flex="45%">
          <ChartContainer>
            {barData ? (
              <Bar options={barOptions(handleBarClicked)} data={barData} />
            ) : (
              <NoData>No Data</NoData>
            )}
          </ChartContainer>
        </Child>
      </Container>
      {showModal ? (
        <SectionModal
          open={showModal}
          facilityName={(woundFacility?.label ?? woundFacility?.[0]?.label)}
          startDate={startDate}
          endDate={endDate}
          closeFn={() => setShowModal(false)}
          resetFn={() => {}}
          isLoading={false}
          tableData={stageDetails}
          tableCols={pressureUlcerStagingDetailCols()}
          title={`${title} - ${stage}`}
          getUserCallback={(item: any) => setClickedIncident(item)}
          handlePdfExport={() =>
            handlePDFDownload({
              data: stageDetails,
              columnData: pressureUlcerStagingDetailCols(),
              fileName: (woundFacility?.label ?? woundFacility?.[0]?.label),
              startDate,
              endDate,
              subTitle: `${title} - ${stage}`,
            })
          }
          handleCsvExport={() =>
            handleCSVDownload(
              stageDetails,
              pressureUlcerStagingDetailCols(),
              `${(woundFacility?.label ?? woundFacility?.[0]?.label)} - ${title} - ${stage}`
            )
          }
        />
      ) : null}
    </LayerContent>
  );
};

export default PressureUlcerStaging;
